export const OFFICE_OPTIONS = [
  { value: "Office of the Chancellor", label: "Office of the Chancellor" },
  {
    value: "Office of the Vice Chancellor for Academic Affairs",
    label: "Office of the Vice Chancellor for Academic Affairs",
  },
  {
    value: "Office of the Vice Chancellor for Finance and Administration",
    label: "Office of the Vice Chancellor for Finance and Administration",
  },
  { value: "Faculty of Education", label: "Faculty of Education" },
  {
    value: "Faculty of Management and Development Studies",
    label: "Faculty of Management and Development Studies",
  },
  {
    value: "Faculty of Information and Communication Studies",
    label: "Faculty of Information and Communication Studies",
  },
];

export const OFFICE_NAME_BY_ABBREVIATION = {
  OC: "Office of the Chancellor",
  OVCAA: "Office of the Vice Chancellor for Academic Affairs",
  OVCFA: "Office of the Vice Chancellor for Finance and Administration",
  FEd: "Faculty of Education",
  FMDS: "Faculty of Management and Development Studies",
  FICS: "Faculty of Information and Communication Studies",
};

export const SUB_OFFICE_OPTIONS_BY_OFFICE = {
  "Office of the Chancellor": [
    "Budget Office",
    "Office of the Legal Counsel",
    "Office of Public Affairs",
    "Information and Communication Technology Development Office",
    "Office of Gender Concerns",
    "Office of Ugnayan ng Pahinungod",
    "Quality Assurance Office",
  ],
  "Office of the Vice Chancellor for Academic Affairs": [
    "Office of the University Registrar",
    "University Library",
    "Office of Student Affairs",
    "Center for Open and Digital Teaching and Learning",
  ],
  "Office of the Vice Chancellor for Finance and Administration": [
    "Accounting Office",
    "Cash Office",
    "Human Resources and Development Office",
    "Supply and Property Management Office",
    "Campus Development and Maintenance Office",
  ],
  "Faculty of Education": [],
  "Faculty of Management and Development Studies": [],
  "Faculty of Information and Communication Studies": [],
};

export const CRITICALITY_LABELS = {
  urgent: "Urgent",
  overdue: "Overdue",
  due_soon: "Due Soon",
  safe: "Safe",
};

export const CRITICALITY_COLORS = {
  urgent: "#ff1f1f",
  overdue: "black",
  due_soon: "orange",
  safe: "#00573F",
};

export const CRITICALITY_PRIORITY_SEQUENCE = [
  null,
  "urgent",
  "overdue",
  "due_soon",
  "safe",
];
export const DEFAULT_CRITICALITY_PRIORITY = "urgent";

export const PRISM_NOTIFICATION_LABELS = {
  overdue: "Overdue",
  expired: "Expired",
  urgent: "Urgent",
  due_soon: "Due Soon",
  upcoming: "Upcoming",
};

export const PRISM_NOTIFICATION_COLORS = {
  overdue: "black",
  expired: "red",
  urgent: "#ff1f1f",
  due_soon: "orange",
  upcoming: "orange",
};

export const PRISM_PRIORITY_ORDER = [
  "urgent",
  "overdue",
  "due_soon",
  "expired",
  "cancelled",
  "disabled",
];

export const getEffectiveSubscriptionStatus = (subscription = {}) =>
  subscription.is_enabled === false
    ? "disabled"
    : subscription.contract_status || subscription.status || null;

export const getPrismPriorityKey = (recordOrKey) => {
  if (!recordOrKey) return null;

  if (typeof recordOrKey === "string") {
    const normalizedKey = recordOrKey.toLowerCase();
    return PRISM_PRIORITY_ORDER.includes(normalizedKey) ? normalizedKey : null;
  }

  if (recordOrKey.is_enabled === false) return "disabled";

  const criticality = String(recordOrKey.criticality || "").toLowerCase();
  if (PRISM_PRIORITY_ORDER.includes(criticality)) {
    return criticality;
  }

  const contractStatus = String(
    recordOrKey.contract_status || recordOrKey.status || "",
  ).toLowerCase();

  return PRISM_PRIORITY_ORDER.includes(contractStatus)
    ? contractStatus
    : null;
};

export const getPrismPriorityRank = (recordOrKey) => {
  const priorityKey = getPrismPriorityKey(recordOrKey);
  const priorityRank = PRISM_PRIORITY_ORDER.indexOf(priorityKey);

  return priorityRank === -1 ? PRISM_PRIORITY_ORDER.length : priorityRank;
};

export const STATUS_COLORS = {
  active: "green",
  expired: "red",
  cancelled: "default",
  disabled: "default",
};

export const STATUS_FILTER_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "expired", label: "Expired" },
  { value: "cancelled", label: "Cancelled" },
  { value: "disabled", label: "Disabled" },
];

export const formatLabel = (value) => {
  if (!value) return "-";

  return String(value)
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

export const formatOfficeName = (value) => {
  if (!value) return "-";

  return OFFICE_NAME_BY_ABBREVIATION[value] || value;
};

export const getOfficeFilterValue = (value) => {
  if (!value) return null;

  return OFFICE_NAME_BY_ABBREVIATION[value] || value;
};

export const getSubOfficeOptions = (office, currentValue = null) => {
  const normalizedOffice = getOfficeFilterValue(office);
  const options =
    SUB_OFFICE_OPTIONS_BY_OFFICE[normalizedOffice]?.map((subOffice) => ({
      value: subOffice,
      label: subOffice,
    })) || [];

  if (
    currentValue &&
    !options.some((option) => option.value === currentValue)
  ) {
    return [
      ...options,
      {
        value: currentValue,
        label: currentValue,
      },
    ];
  }

  return options;
};

export const officeHasSubOffices = (office) =>
  getSubOfficeOptions(office).length > 0;

const UNASSIGNED_OFFICE_KEY = "__unassigned_office__";
const UNASSIGNED_SUB_DEPARTMENT_KEY = "__unassigned_sub_department__";

export const groupPrismSubscriptionsByOffice = (subscriptions = []) => {
  const officeOrder = new Map(
    OFFICE_OPTIONS.map((office, index) => [
      getOfficeFilterValue(office.value),
      index,
    ]),
  );
  const groupsByOffice = new Map();

  subscriptions.forEach((subscription) => {
    const normalizedOffice = getOfficeFilterValue(
      subscription.subscriber_department,
    );
    const hasOffice = Boolean(normalizedOffice);
    const officeKey = hasOffice ? normalizedOffice : UNASSIGNED_OFFICE_KEY;

    if (!groupsByOffice.has(officeKey)) {
      const configuredSubDepartments = hasOffice
        ? getSubOfficeOptions(normalizedOffice).map((option) => option.value)
        : [];

      groupsByOffice.set(officeKey, {
        key: officeKey,
        label: hasOffice ? formatOfficeName(normalizedOffice) : "Unassigned Office",
        count: 0,
        hasSubDepartments: configuredSubDepartments.length > 0,
        configuredSubDepartments,
        records: [],
        subDepartmentsByKey: new Map(),
      });
    }

    const officeGroup = groupsByOffice.get(officeKey);
    officeGroup.count += 1;

    if (!officeGroup.hasSubDepartments) {
      officeGroup.records.push(subscription);
      return;
    }

    const subDepartment = String(
      subscription.subscriber_sub_department || "",
    ).trim();
    const subDepartmentKey = subDepartment || UNASSIGNED_SUB_DEPARTMENT_KEY;

    if (!officeGroup.subDepartmentsByKey.has(subDepartmentKey)) {
      officeGroup.subDepartmentsByKey.set(subDepartmentKey, {
        key: `${officeKey}:${subDepartmentKey}`,
        label: subDepartment || officeGroup.label,
        records: [],
      });
    }

    officeGroup.subDepartmentsByKey
      .get(subDepartmentKey)
      .records.push(subscription);
  });

  return Array.from(groupsByOffice.values())
    .sort((a, b) => {
      if (a.key === UNASSIGNED_OFFICE_KEY) return 1;
      if (b.key === UNASSIGNED_OFFICE_KEY) return -1;

      const aOrder = officeOrder.get(a.key);
      const bOrder = officeOrder.get(b.key);
      const aIsConfigured = aOrder !== undefined;
      const bIsConfigured = bOrder !== undefined;

      if (aIsConfigured && bIsConfigured) return aOrder - bOrder;
      if (aIsConfigured) return -1;
      if (bIsConfigured) return 1;
      return String(a.label).localeCompare(String(b.label));
    })
    .map(({ configuredSubDepartments, subDepartmentsByKey, ...group }) => {
      const subDepartmentOrder = new Map(
        configuredSubDepartments.map((subDepartment, index) => [
          subDepartment,
          index,
        ]),
      );
      const subDepartments = Array.from(subDepartmentsByKey.values()).sort(
        (a, b) => {
          const aIsUnassigned = a.key.endsWith(
            `:${UNASSIGNED_SUB_DEPARTMENT_KEY}`,
          );
          const bIsUnassigned = b.key.endsWith(
            `:${UNASSIGNED_SUB_DEPARTMENT_KEY}`,
          );

          if (aIsUnassigned) return -1;
          if (bIsUnassigned) return 1;

          const aOrder = subDepartmentOrder.get(a.label);
          const bOrder = subDepartmentOrder.get(b.label);
          const aIsConfigured = aOrder !== undefined;
          const bIsConfigured = bOrder !== undefined;

          if (aIsConfigured && bIsConfigured) return aOrder - bOrder;
          if (aIsConfigured) return -1;
          if (bIsConfigured) return 1;
          return String(a.label).localeCompare(String(b.label));
        },
      );

      return {
        ...group,
        subDepartments,
      };
    });
};

export const formatSubOfficeName = (value) => value || "Office-level (no unit)";

export const formatAttentionScopeName = (subscription = {}) => {
  const unit = String(subscription.subscriber_sub_department || "").trim();

  return unit || formatOfficeName(subscription.subscriber_department);
};

export const formatOfficeWithSubOffice = (subscription = {}) => {
  const office = formatOfficeName(subscription.subscriber_department);
  const subOffice = subscription.subscriber_sub_department;

  if (!subOffice) {
    return office;
  }

  return `${office} / ${formatSubOfficeName(subOffice)}`;
};

const getAdminRoles = (details = {}) =>
  Array.isArray(details?.admin_roles) ? details.admin_roles : [];

const uniqueOptionsByValue = (options) =>
  Array.from(
    options
      .reduce((optionsByValue, option) => {
        if (option?.value && !optionsByValue.has(option.value)) {
          optionsByValue.set(option.value, option);
        }

        return optionsByValue;
      }, new Map())
      .values(),
  );

export const isPrismXadmin = (details = {}) =>
  getAdminRoles(details).some((role) => role.role === "xadmin");

export const isPrismGlobalAdmin = (details = {}) =>
  getAdminRoles(details).some((role) => role.role === "prismGlobalAdmin");

export const hasGlobalPrismAccess = (details = {}) =>
  isPrismGlobalAdmin(details);

export const getPrismAdminScopes = (details = {}) => {
  if (hasGlobalPrismAccess(details)) return [];

  const scopesByKey = new Map();
  const validOffices = new Set(OFFICE_OPTIONS.map((option) => option.value));

  getAdminRoles(details)
    .filter((role) => role.role === "prismAdmin" && role.office)
    .forEach((role) => {
      const office = getOfficeFilterValue(role.office);
      const unit = String(role.unit || "").trim() || null;
      const validUnits = getSubOfficeOptions(office).map((option) => option.value);

      if (!validOffices.has(office) || (unit && !validUnits.includes(unit))) {
        return;
      }

      scopesByKey.set(`${office}|${unit || ""}`, { office, unit });
    });

  return Array.from(scopesByKey.values());
};

export const getPrismAdminOffices = (details = {}) =>
  Array.from(new Set(getPrismAdminScopes(details).map((scope) => scope.office)));

export const getSinglePrismAdminScope = (details = {}) => {
  if (hasGlobalPrismAccess(details)) return null;

  const scopes = getPrismAdminScopes(details);
  return scopes.length === 1 ? scopes[0] : null;
};

export const getSingleOfficePrismAdminOffice = (details = {}) => {
  if (hasGlobalPrismAccess(details)) return null;

  const scope = getSinglePrismAdminScope(details);
  return scope?.unit === null ? scope.office : null;
};

export const canManagePrismOffice = (details = {}, office) => {
  return canManagePrismScope(details, office, null);
};

export const canManagePrismScope = (details = {}, office, unit = null) => {
  if (hasGlobalPrismAccess(details)) return true;

  const normalizedOffice = getOfficeFilterValue(office);
  const normalizedUnit = String(unit || "").trim() || null;

  return getPrismAdminScopes(details).some(
    (scope) =>
      scope.office === normalizedOffice && scope.unit === normalizedUnit,
  );
};

export const canManageAnyPrismOffice = (details = {}) =>
  hasGlobalPrismAccess(details) || getPrismAdminScopes(details).length > 0;

export const isFullPrismRecord = (record = {}, details = {}) => {
  if (record?.prism_view_scope) {
    return record.prism_view_scope === "full";
  }

  return canManagePrismScope(
    details,
    record?.subscriber_department,
    record?.subscriber_sub_department,
  );
};

export const isLimitedPrismRecord = (record = {}, details = {}) => {
  if (record?.prism_view_scope) {
    return record.prism_view_scope === "limited";
  }

  return !isFullPrismRecord(record, details);
};

export const LIMITED_PRISM_SUBSCRIPTION_FIELD_KEYS = new Set([
  "subscription_name",
  "subscription_details",
  "subscriber_department",
  "subscriber_sub_department",
]);

export const canManagePrismRecord = (details = {}, record = {}) => {
  if (typeof record?.prism_can_manage === "boolean") {
    return record.prism_can_manage;
  }

  return canManagePrismScope(
    details,
    record?.subscriber_department,
    record?.subscriber_sub_department,
  );
};

export const renderRestrictedValue = (
  record = {},
  value,
  details = {},
  formatter = (fieldValue) => fieldValue || "-",
) => {
  if (isLimitedPrismRecord(record, details)) {
    return null;
  }

  return formatter(value);
};

export const buildPrismManageOfficeOptions = (
  details = {},
  currentOffice = null,
) => {
  if (hasGlobalPrismAccess(details)) {
    return OFFICE_OPTIONS;
  }

  const manageableOfficeOptions = getPrismAdminOffices(details).map((office) => ({
    value: office,
    label: formatOfficeName(office),
  }));
  const normalizedCurrentOffice = getOfficeFilterValue(currentOffice);

  if (
    normalizedCurrentOffice &&
    canManagePrismOffice(details, normalizedCurrentOffice)
  ) {
    manageableOfficeOptions.push({
      value: currentOffice,
      label: formatOfficeName(currentOffice),
    });
  }

  return uniqueOptionsByValue(manageableOfficeOptions);
};

export const buildPrismManageUnitOptions = (
  details = {},
  office,
  currentUnit = null,
) => {
  if (hasGlobalPrismAccess(details)) {
    return getSubOfficeOptions(office, currentUnit);
  }

  const normalizedOffice = getOfficeFilterValue(office);
  const options = getPrismAdminScopes(details)
    .filter((scope) => scope.office === normalizedOffice && scope.unit)
    .map((scope) => ({ value: scope.unit, label: scope.unit }));

  if (
    currentUnit &&
    canManagePrismScope(details, normalizedOffice, currentUnit)
  ) {
    options.push({ value: currentUnit, label: currentUnit });
  }

  return uniqueOptionsByValue(options);
};

export const buildPrismCreateUnitOptions = (
  details = {},
  office,
  currentUnit = null,
) => {
  if (hasGlobalPrismAccess(details)) {
    return getSubOfficeOptions(office, currentUnit);
  }

  const normalizedOffice = getOfficeFilterValue(office);
  const officeScopes = getPrismAdminScopes(details).filter(
    (scope) => scope.office === normalizedOffice,
  );

  if (officeScopes.some((scope) => scope.unit === null)) {
    return getSubOfficeOptions(normalizedOffice, currentUnit);
  }

  return uniqueOptionsByValue(
    officeScopes
      .filter((scope) => scope.unit)
      .map((scope) => ({ value: scope.unit, label: scope.unit })),
  );
};

export const canCreatePrismScope = (details = {}, office, unit = null) => {
  if (hasGlobalPrismAccess(details)) return true;

  const normalizedOffice = getOfficeFilterValue(office);
  const normalizedUnit = String(unit || "").trim() || null;

  return getPrismAdminScopes(details).some(
    (scope) =>
      scope.office === normalizedOffice &&
      (scope.unit === null || scope.unit === normalizedUnit),
  );
};

export const buildOfficeFilterOptions = (subscriptions = []) => {
  const optionsByValue = new Map(
    OFFICE_OPTIONS.map((office) => [
      getOfficeFilterValue(office.value),
      { ...office, value: getOfficeFilterValue(office.value) },
    ]),
  );

  subscriptions.forEach((subscription) => {
    const office = subscription.subscriber_department;
    const filterValue = getOfficeFilterValue(office);

    if (filterValue && !optionsByValue.has(filterValue)) {
      optionsByValue.set(filterValue, {
        value: filterValue,
        label: formatOfficeName(office),
      });
    }
  });

  return Array.from(optionsByValue.values());
};

export const parseDateOnly = (value) => {
  if (typeof value === "string") {
    const dateOnlyMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);

    if (dateOnlyMatch) {
      const [, year, month, day] = dateOnlyMatch;
      return new Date(Number(year), Number(month) - 1, Number(day));
    }
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  date.setHours(0, 0, 0, 0);
  return date;
};

export const getRenewalDateRangeError = (
  renewalDateValue,
  contractStartDateValue,
  contractEndDateValue,
) => {
  const renewalDate = parseDateOnly(renewalDateValue);

  if (!renewalDate) return null;

  const contractStartDate = parseDateOnly(contractStartDateValue);
  if (contractStartDate && renewalDate < contractStartDate) {
    return "Renewal date must be on or after start date.";
  }

  const contractEndDate = parseDateOnly(contractEndDateValue);
  if (contractEndDate && renewalDate > contractEndDate) {
    return "Renewal date must be on or before contract end date.";
  }

  return null;
};

export const shouldReactivateExpiredSubscription = (
  originalStatus,
  originalContractEndDateValue,
  nextContractEndDateValue,
  todayValue = new Date(),
) => {
  if (originalStatus !== "expired") return false;

  const originalContractEndDate = parseDateOnly(originalContractEndDateValue);
  const nextContractEndDate = parseDateOnly(nextContractEndDateValue);
  const today = parseDateOnly(todayValue);

  if (!originalContractEndDate || !nextContractEndDate || !today) {
    return false;
  }

  return originalContractEndDate < today && nextContractEndDate >= today;
};

export const formatDate = (value) => {
  if (!value) return "-";

  if (typeof value === "string") {
    const dateOnlyMatch = value.match(/^(\d{4})-(\d{2})-(\d{2})/);

    if (dateOnlyMatch) {
      const [, year, month, day] = dateOnlyMatch;
      return `${Number(month)}/${Number(day)}/${year}`;
    }
  }

  const date = parseDateOnly(value);

  if (!date) {
    return value;
  }

  return date.toLocaleDateString();
};

export const getDateTime = (value) => {
  const parsedDate = parseDateOnly(value);
  return parsedDate ? parsedDate.getTime() : Infinity;
};

export const getCriticality = (
  renewalDateValue,
  subscriptionType,
  contractStatus,
) => {
  if (contractStatus !== "active") return null;
  if (!renewalDateValue) return null;

  const renewalDate = parseDateOnly(renewalDateValue);
  if (!renewalDate) return null;

  if (subscriptionType === "free") return "safe";

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (renewalDate < today) return "overdue";

  const oneMonthFromNow = new Date(today);
  oneMonthFromNow.setMonth(today.getMonth() + 1);
  if (renewalDate <= oneMonthFromNow) return "urgent";

  const threeMonthsFromNow = new Date(today);
  threeMonthsFromNow.setMonth(today.getMonth() + 3);
  if (renewalDate <= threeMonthsFromNow) return "due_soon";

  return "safe";
};

export const buildPrismNotifications = (subscriptions = []) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const threeMonthsFromNow = new Date(today);
  threeMonthsFromNow.setMonth(today.getMonth() + 3);

  const notifications = [];

  subscriptions.forEach((subscription, index) => {
    const subscriptionName = subscription.subscription_name || "Subscription";
    const office = formatOfficeName(subscription.subscriber_department);
    const renewalDate = parseDateOnly(subscription.contract_renewal_date);
    const endDate = parseDateOnly(subscription.contract_end_date);
    const criticality = getCriticality(
      subscription.contract_renewal_date,
      subscription.subscription_type,
      subscription.contract_status,
    );

    if (
      criticality === "overdue" ||
      criticality === "urgent" ||
      criticality === "due_soon"
    ) {
      notifications.push({
        key: `${subscription.id || index}-renewal-${criticality}`,
        type:
          criticality === "overdue"
            ? "overdue_renewal"
            : criticality === "urgent"
              ? "urgent_renewal"
              : "due_soon_renewal",
        severity: criticality,
        subscription,
        subscriptionName,
        office,
        date: subscription.contract_renewal_date,
        dateTime: renewalDate ? renewalDate.getTime() : Infinity,
        message:
          criticality === "overdue"
            ? "Renewal is overdue"
            : criticality === "urgent"
              ? "Renewal is due within 1 month"
              : "Renewal is due within 3 months",
      });
    }

    const isExpired =
      subscription.contract_status === "expired" ||
      Boolean(endDate && endDate < today);

    if (isExpired) {
      notifications.push({
        key: `${subscription.id || index}-contract-expired`,
        type: "expired_contract",
        severity: "expired",
        subscription,
        subscriptionName,
        office,
        date: subscription.contract_end_date,
        dateTime: endDate ? endDate.getTime() : Infinity,
        message: "Contract is expired",
      });
      return;
    }

    const isNearContractEnd =
      subscription.contract_status === "active" &&
      endDate &&
      endDate >= today &&
      endDate <= threeMonthsFromNow;

    if (isNearContractEnd) {
      notifications.push({
        key: `${subscription.id || index}-contract-upcoming`,
        type: "near_contract_end",
        severity: "upcoming",
        subscription,
        subscriptionName,
        office,
        date: subscription.contract_end_date,
        dateTime: endDate.getTime(),
        message: "Contract ends soon",
      });
    }
  });

  return notifications.sort((a, b) => {
    const priorityA = getPrismPriorityRank(a.severity);
    const priorityB = getPrismPriorityRank(b.severity);

    if (priorityA !== priorityB) {
      return priorityA - priorityB;
    }

    if (a.dateTime !== b.dateTime) {
      return a.dateTime - b.dateTime;
    }

    return String(a.subscriptionName).localeCompare(
      String(b.subscriptionName),
    );
  });
};
